import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Settings, Globe, MapPin, Monitor, RefreshCw, Save, Check } from 'lucide-react'
import { useApi } from '../../hooks/useApi'
import DataSources from './DataSources'
import { ThemeSelect } from '../../features/reportThemes/ThemeSelect'
import { AgencySelect } from '../../features/partnerAgencies/AgencySelect'

interface Client {
  id: number
  name: string
  domain: string
  country: string
  locationCity: string | null
  language: string
  defaultDevice: string
  autoCheck: boolean
  defaultThemeId: number | null
  agencyId: number | null
}

const COUNTRIES = [
  { code: 'fr', label: 'France' },
  { code: 'us', label: 'Etats-Unis' },
  { code: 'gb', label: 'Royaume-Uni' },
  { code: 'de', label: 'Allemagne' },
  { code: 'es', label: 'Espagne' },
  { code: 'it', label: 'Italie' },
  { code: 'be', label: 'Belgique' },
  { code: 'ch', label: 'Suisse' },
  { code: 'ca', label: 'Canada' },
  { code: 'nl', label: 'Pays-Bas' },
  { code: 'pt', label: 'Portugal' },
  { code: 'lu', label: 'Luxembourg' },
]

const LANGUAGES = [
  { code: 'fr', label: 'Francais' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Espanol' },
  { code: 'it', label: 'Italiano' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'pt', label: 'Portugues' },
]

export default function ProjectSettings() {
  const { projectId } = useParams()
  const { data: client, loading, refetch } = useApi<Client>(`/api/clients/${projectId}`)

  const [form, setForm] = useState({
    name: '',
    domain: '',
    country: 'fr',
    locationCity: '',
    language: 'fr',
    defaultDevice: 'desktop',
    autoCheck: false,
    defaultThemeId: null as number | null,
    agencyId: null as number | null,
  })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [activeTab, setActiveTab] = useState<'general' | 'sources'>('general')

  useEffect(() => {
    if (client) {
      setForm({
        name: client.name,
        domain: client.domain,
        country: client.country || 'fr',
        locationCity: client.locationCity || '',
        language: client.language || 'fr',
        defaultDevice: client.defaultDevice || 'desktop',
        autoCheck: client.autoCheck ?? false,
        defaultThemeId: client.defaultThemeId ?? null,
        agencyId: client.agencyId ?? null,
      })
    }
  }, [client])

  const handleSave = async () => {
    setSaving(true)
    setSaved(false)
    try {
      await fetch(`/api/clients/${projectId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      setSaved(true)
      refetch()
      setTimeout(() => setSaved(false), 3000)
    } catch {}
    setSaving(false)
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-[#8B8FA7]" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-[900px] mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#1B2559]">Parametres du projet</h1>
        <p className="text-[#8B8FA7] mt-1">{client?.domain}</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#F8F9FC] rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('general')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'general'
              ? 'bg-white text-[#1B2559] shadow-sm'
              : 'text-[#8B8FA7] hover:text-[#1B2559]'
          }`}
        >
          <span className="flex items-center gap-2">
            <Settings size={16} strokeWidth={1.25} />
            General
          </span>
        </button>
        <button
          onClick={() => setActiveTab('sources')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'sources'
              ? 'bg-white text-[#1B2559] shadow-sm'
              : 'text-[#8B8FA7] hover:text-[#1B2559]'
          }`}
        >
          <span className="flex items-center gap-2">
            <Globe size={16} strokeWidth={1.25} />
            Sources de donnees
          </span>
        </button>
      </div>

      {activeTab === 'general' ? (
        <div className="bg-white rounded-xl border border-[#E2E4ED] p-6 space-y-6">
          {/* Nom + Domaine */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#1B2559] mb-1.5">Nom du projet</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-[#E2E4ED] text-sm text-[#1B2559] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 focus:border-[#6366F1]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1B2559] mb-1.5">Domaine</label>
              <input
                type="text"
                value={form.domain}
                onChange={(e) => setForm({ ...form, domain: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-[#E2E4ED] text-sm text-[#1B2559] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 focus:border-[#6366F1]"
              />
            </div>
          </div>

          {/* Pays + Ville */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#1B2559] mb-1.5">
                <span className="flex items-center gap-1.5"><MapPin size={14} strokeWidth={1.25} /> Pays</span>
              </label>
              <select
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-[#E2E4ED] text-sm text-[#1B2559] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 focus:border-[#6366F1]"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1B2559] mb-1.5">Ville (optionnel)</label>
              <input
                type="text"
                value={form.locationCity}
                onChange={(e) => setForm({ ...form, locationCity: e.target.value })}
                placeholder="Ex: Lyon, Marseille..."
                className="w-full px-3 py-2 rounded-lg border border-[#E2E4ED] text-sm text-[#1B2559] placeholder:text-[#8B8FA7] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 focus:border-[#6366F1]"
              />
            </div>
          </div>

          {/* Langue + Device */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#1B2559] mb-1.5">Langue</label>
              <select
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-[#E2E4ED] text-sm text-[#1B2559] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 focus:border-[#6366F1]"
              >
                {LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1B2559] mb-1.5">
                <span className="flex items-center gap-1.5"><Monitor size={14} strokeWidth={1.25} /> Appareil par defaut</span>
              </label>
              <select
                value={form.defaultDevice}
                onChange={(e) => setForm({ ...form, defaultDevice: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-[#E2E4ED] text-sm text-[#1B2559] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 focus:border-[#6366F1]"
              >
                <option value="desktop">Desktop</option>
                <option value="mobile">Mobile</option>
              </select>
            </div>
          </div>

          {/* Auto check */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setForm({ ...form, autoCheck: !form.autoCheck })}
              className={`relative w-11 h-6 rounded-full transition-colors ${
                form.autoCheck ? 'bg-[#6366F1]' : 'bg-[#E2E4ED]'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  form.autoCheck ? 'translate-x-5' : ''
                }`}
              />
            </button>
            <div>
              <p className="text-sm font-medium text-[#1B2559]">Verification automatique quotidienne</p>
              <p className="text-xs text-[#8B8FA7]">Les positions seront verifiees chaque nuit a minuit</p>
            </div>
          </div>

          {/* Agence partenaire */}
          <div className="max-w-sm">
            <label className="block text-sm font-medium text-[#1B2559] mb-1.5">Agence partenaire</label>
            <AgencySelect
              value={form.agencyId}
              onChange={(agencyId) => setForm({ ...form, agencyId })}
              ariaLabel="Agence partenaire"
              className="w-full px-3 py-2 rounded-lg border border-[#E2E4ED] text-sm text-[#1B2559] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 focus:border-[#6366F1]"
            />
          </div>

          {/* Thème de rapport par défaut */}
          <div className="max-w-sm">
            <ThemeSelect
              label="Thème de rapport par défaut"
              value={form.defaultThemeId}
              onChange={(id) => setForm({ ...form, defaultThemeId: id })}
            />
            <p className="mt-1.5 text-xs text-[#8B8FA7]">Appliqué aux rapports de ce client, sauf thème défini sur le rapport.</p>
          </div>

          {/* Save button */}
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <RefreshCw size={16} className="animate-spin" />
              ) : saved ? (
                <Check size={16} />
              ) : (
                <Save size={16} strokeWidth={1.25} />
              )}
              {saving ? 'Sauvegarde...' : saved ? 'Sauvegarde !' : 'Sauvegarder'}
            </button>
            {saved && (
              <span className="text-sm text-[#10B981]">Parametres mis a jour</span>
            )}
          </div>
        </div>
      ) : (
        <DataSources />
      )}
    </div>
  )
}
